import { useEffect, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_RETURN_TRANSITION_MS,
  selectActiveLabels,
  selectLabelFocus,
  type CameraPose,
} from './engine/camera-playback';
import { useTimedFrame } from './engine/useTimedFrame';
import { setSfxEnabled } from './sfx';
import { setTtsBridge, useSlidePackPlayer } from './engine/useSlidePackPlayer';
import type { PlayerConfig } from './ports';
import { CameraPlaybackProvider, type ElementCameraPlayback } from './render/camera-playback-context';
import { SlideStage } from './render/SlideStage';
import type { McqContent, ScriptElement, Slide, V2Pack } from './schema';

interface Props {
  pack: V2Pack;
  config?: PlayerConfig;
  /** Auto-load + start on mount. Default true. */
  autoStart?: boolean;
  /** Start playback at this slide index (e.g. resume / preview a slide). */
  startIndex?: number;
  /** Seek: start playback at this reveal step of the entry slide. Changing it
   *  re-seeks (prior steps reveal instantly). */
  startStepIndex?: number;
  /** Fires when the current reveal step changes — lets a host drive a seekbar. */
  onStepChange?: (info: { slideIndex: number; stepIndex: number; stepId: string | null }) => void;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Show ONE canvas at a time, zoomed to fill `focusHeight`, instead of stacking
   * every canvas into one tall scrolling board. See `SlideStage.focusCanvas`.
   */
  focusCanvas?: boolean;
  /** Height (px) a focused canvas is fitted into. Required with `focusCanvas`. */
  focusHeight?: number;
  /** Play the writing animation even on slides with no reveal steps. */
  animateOnShowAll?: boolean;
  /** Host-controlled canvas index, overriding the automatic pager. */
  canvasIndex?: number | null;
  /** Reports which canvas is shown and how many there are, for host controls. */
  onCanvasChange?: (info: { index: number; total: number }) => void;
  /** Host-chosen slide index — jumping here restarts playback at that slide. */
  slideIndex?: number | null;
  /** Reports the slide being played and how many there are, for host controls. */
  onSlideChange?: (info: { index: number; total: number }) => void;
}

/** How long the player may sit idle with a pack loaded before we conclude
 *  playback died (aborted by a reload) and restart it. Long enough not to race
 *  the normal load→start handoff. */
const IDLE_REVIVE_MS = 2000;

/** Every 3D element on a slide + the home pose it returns to. */
function threeDOf(slide: Slide | null): { id: string; defaultPose: CameraPose }[] {
  const out: { id: string; defaultPose: CameraPose }[] = [];
  slide?.rows.forEach((row) =>
    row.columns.forEach((col) =>
      col.elements.forEach((el) => {
        if (el.type !== '3d') return;
        const data = el.content && typeof el.content === 'object' ? (el.content as Record<string, unknown>) : null;
        out.push({
          id: el.id,
          defaultPose: {
            cameraOrbit: (data?.defaultCameraOrbit as string) ?? '0deg 75deg 105%',
            cameraTarget: (data?.defaultCameraTarget as string) ?? 'auto auto auto',
            fieldOfView: data?.fieldOfView as string | undefined,
          },
        });
      }),
    ),
  );
  return out;
}

function findMcq(slide: Slide | null): { element: ScriptElement; content: McqContent } | null {
  if (!slide) return null;
  for (const row of slide.rows)
    for (const col of row.columns)
      for (const el of col.elements)
        if (el.type === 'mcq' && el.content && typeof el.content === 'object')
          return { element: el, content: el.content as McqContent };
  return null;
}

/**
 * The shared, host-agnostic slide player: drives the reveal/quiz/pace/confidence
 * engine, renders the shared `SlideStage`, and animates the 3D camera per step.
 * Platform features come from `config` ports (TTS, background, …). Quiz/pace/
 * confidence are answerable via minimal built-in controls so it's playable
 * without a clicker.
 */
export function SlidePlayer({
  pack,
  config,
  autoStart = true,
  startIndex,
  startStepIndex,
  onStepChange,
  className,
  style,
  focusCanvas = false,
  focusHeight = 0,
  animateOnShowAll = false,
  canvasIndex = null,
  onCanvasChange,
  slideIndex: slideIndexProp = null,
  onSlideChange,
}: Props) {
  const player = useSlidePackPlayer();

  // Inject the host TTS bridge (clip-less narration). Absent → silent.
  useEffect(() => {
    setTtsBridge(config?.tts ?? null);
    return () => setTtsBridge(null);
  }, [config?.tts]);

  // Load the pack, then start in a SEPARATE effect once `pack` state is set —
  // `start()` reads the pack from state, so starting in the same tick as
  // loadPack() would see a stale null pack (→ blank board).
  const { loadPack, start, startFrom, startFromStep, pack: loadedPack } = player;
  useEffect(() => {
    loadPack(pack);
  }, [pack, loadPack]);

  const startedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loadedPack || !autoStart) return;
    if (startedRef.current === loadedPack._id) return;
    startedRef.current = loadedPack._id;
    if (typeof startStepIndex === 'number') startFromStep(startIndex ?? 0, startStepIndex);
    else if (typeof startIndex === 'number') startFrom(startIndex);
    else start();
  }, [loadedPack, autoStart, startIndex, startStepIndex, start, startFrom, startFromStep]);

  // Re-seek when the host changes startStepIndex (drag the seekbar) after the
  // initial start — restart playback from that step (prior steps reveal instantly).
  const seekRef = useRef<number | undefined>(startStepIndex);
  useEffect(() => {
    if (!loadedPack || !autoStart) return;
    if (startStepIndex === seekRef.current) return;
    seekRef.current = startStepIndex;
    if (typeof startStepIndex === 'number') startFromStep(startIndex ?? 0, startStepIndex);
  }, [startStepIndex, loadedPack, autoStart, startIndex, startFromStep]);

  // Report step changes so a host can position a timeline seekbar.
  const stepIndex = player.currentRevealStepIndex;
  const slideIndex = player.currentSlideIndex;
  const stepId = player.currentRevealStep?.id ?? null;
  useEffect(() => {
    onStepChange?.({ slideIndex, stepIndex, stepId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideIndex, stepIndex, stepId]);

  const slide = player.currentSlide;
  const revealed = player.revealedElementIds;
  const threeD = useMemo(() => threeDOf(slide), [slide]);

  // Revive a dead player. `loadPack` aborts playback and drops the pack back to
  // 'idle'; the effect above then refuses to restart it, because `startedRef`
  // still holds this pack's id. Anything that reloads the pack mid-session
  // therefore left the board frozen with nothing revealed — which, now that an
  // un-revealed scripted slide correctly shows nothing, reads as a blank
  // preview. Restart rather than paper over it: revealing the whole slide as a
  // fallback would wreck the reveal order on any slide whose first cue is slow.
  useEffect(() => {
    if (!loadedPack || !autoStart) return undefined;
    if (player.phase !== 'idle') return undefined;
    const t = window.setTimeout(() => {
      startedRef.current = null; // allow the start effect to fire again
      start();
    }, IDLE_REVIVE_MS);
    return () => window.clearTimeout(t);
  }, [loadedPack, autoStart, player.phase, start]);

  // Report the slide position so a host can draw "‹ slide 4 of 10 ›".
  const slideTotal = loadedPack?.slides.length ?? 0;
  useEffect(() => {
    onSlideChange?.({ index: slideIndex, total: slideTotal });
  }, [slideIndex, slideTotal, onSlideChange]);

  // Host jumped to a slide with the arrows.
  useEffect(() => {
    if (slideIndexProp == null || !loadedPack) return;
    if (slideIndexProp === slideIndex) return;
    if (slideIndexProp < 0 || slideIndexProp >= loadedPack.slides.length) return;
    startFrom(slideIndexProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideIndexProp, loadedPack]);

  // Carry on to the next slide when a slide ends with no authored next-beat.
  // The engine routes purely by beat id (pickNextSlideIndex → beatMeta.next),
  // so a pack with no routing — which is every pack authored so far — stops
  // dead after its first slide. Where routing DOES exist the engine has already
  // followed it, so we stand aside.

  // The latest reveal step whose elements are all revealed — its keyframes drive
  // the camera. We key off the engine's ACTUAL current reveal step (incl.
  // narration-only steps, where camera keyframes commonly live), with a
  // wall-clock from when that step started.
  const currentStep = player.currentRevealStep;
  const stepStartRef = useRef(0);
  useEffect(() => {
    stepStartRef.current = typeof performance !== 'undefined' ? performance.now() : 0;
  }, [currentStep?.id]);

  const [cameraPlayback, setCameraPlayback] = useState<Record<string, ElementCameraPlayback> | null>(
    null,
  );
  const sigRef = useRef('');
  useEffect(() => {
    if (!slide || threeD.length === 0) {
      setCameraPlayback(null);
      sigRef.current = '';
      return undefined;
    }
    let raf = 0;
    const tick = () => {
      const kfs = currentStep?.cameraKeyframes ?? [];
      const now = (typeof performance !== 'undefined' ? performance.now() : 0) - stepStartRef.current;
      const map: Record<string, ElementCameraPlayback> = {};
      for (const el of threeD) {
        const elKfs = kfs.filter((k) => k.targetElementId === el.id);
        if (elKfs.length === 0) continue;
        const focus = selectLabelFocus(elKfs, now);
        const pose = focus
          ? focus.pose
          : { cameraOrbit: el.defaultPose.cameraOrbit, cameraTarget: 'auto auto auto', fieldOfView: el.defaultPose.fieldOfView };
        map[el.id] = { pose, labels: selectActiveLabels(elKfs, now), transitionMs: focus ? focus.transitionMs : DEFAULT_RETURN_TRANSITION_MS };
      }
      const sig = JSON.stringify(map);
      if (sig !== sigRef.current) {
        sigRef.current = sig;
        setCameraPlayback(map);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [slide, threeD, currentStep]);

  // Word-timed playback frame (elements/annotations/strokes/spotlights gated by
  // the narration clock while the current step carries timing) + reveal SFX —
  // the same hook the Electron classroom uses, so playback is identical.
  const frame = useTimedFrame({
    slide,
    currentStep,
    currentStepIndex: player.currentRevealStepIndex,
    baseRevealed: player.revealedElementIds,
    baseAnnotations: player.accumulatedAnnotations,
    fireSfx: true,
  });

  // When timed reveals are active, pass the set verbatim (an EMPTY set means
  // "nothing yet" — not "show all"); otherwise keep the engine's convention
  // where an empty set → null → show everything (slides with no reveal steps).
  const displayRevealed: Set<string> | null = frame.timingActive
    ? frame.revealedElementIds
    : revealed.size > 0
      ? revealed
      : null;

  // NOTE: releasing elements that belong to no reveal step now happens inside
  // SlideStage — it has to be gated on which canvas the pager is showing, and
  // only the stage knows that. Releasing them here dragged the view forward.
  const displayAnnotations = frame.annotations;

  useEffect(() => {
    setSfxEnabled(config?.sfx !== false);
  }, [config?.sfx]);

  const background = config?.background ?? 'chalkboard';
  const isChalk = background === 'chalkboard';
  const surfaceBg = isChalk ? undefined : background === 'white' ? '#ffffff' : background;

  const mcq = player.phase === 'quiz_waiting' ? findMcq(slide) : null;

  return (
    <div
      className={`${isChalk ? 'chalk-board ' : ''}${className ?? ''}`}
      style={{ width: '100%', background: surfaceBg, ...style }}
    >
      <CameraPlaybackProvider value={cameraPlayback}>
        <SlideStage
          slide={slide ?? { id: '', label: '', revealSteps: [], rows: [] }}
          revealedElementIds={displayRevealed}
          annotations={displayAnnotations}
          drawStrokes={frame.drawStrokes}
          spotlights={frame.spotlights}
          spotlight={player.activeSpotlight}
          paused={player.isPaused}
          hideMcq={player.phase === 'quiz_waiting'}
          focusCanvas={focusCanvas}
          focusHeight={focusHeight}
          animateOnShowAll={animateOnShowAll}
          canvasIndex={canvasIndex}
          onCanvasChange={onCanvasChange}
        />
      </CameraPlaybackProvider>

      {/* Minimal interactive controls (no clicker needed). */}
      {mcq && (
        <div className="flex flex-wrap justify-center gap-2 p-3">
          {mcq.content.options.map((opt, i) => (
            <button
              key={i}
              onClick={() => player.answerQuiz(i)}
              className="rounded-md border px-3 py-1.5 text-sm"
              style={{ borderColor: 'rgba(60,45,25,0.25)' }}
            >
              {String.fromCharCode(65 + i)}. {opt}
            </button>
          ))}
        </div>
      )}
      {player.phase === 'pace_waiting' && (
        <div className="flex justify-center gap-2 p-3">
          <button onClick={() => player.answerPaceCheck(true)} className="rounded-md border px-4 py-1.5 text-sm">
            Yes, continue
          </button>
          <button onClick={() => player.answerPaceCheck(false)} className="rounded-md border px-4 py-1.5 text-sm">
            No, re-explain
          </button>
        </div>
      )}
      {player.phase === 'confidence_waiting' && (
        <div className="flex justify-center gap-2 p-3">
          {(['high', 'medium', 'low'] as const).map((lvl) => (
            <button
              key={lvl}
              onClick={() => player.answerConfidence(lvl)}
              className="rounded-md border px-4 py-1.5 text-sm capitalize"
            >
              {lvl}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
